import React, { useState, useEffect, useRef } from "react";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import "./MobileTagDropdown.css";

/**
 * A mobile-optimized tag dropdown with search functionality.
 * Designed for touch interfaces with larger tap targets.
 */
export default function MobileTagDropdown({ 
  tags = [],           // Currently selected tags
  tagsList = [],       // Available tags to choose from
  onAddTag,            // Callback when a tag is added
  onRemoveTag,         // Callback when a tag is removed
  onCreateTag,         // Optional: Callback to create a new tag
  placeholder = "Search or add tags..."
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Filter out already selected tags and apply search filter
  const availableTags = tagsList.filter(t => {
    const notSelected = !tags.some(selected => selected.id === t.id);
    const matchesSearch = t.tag?.toLowerCase().includes(searchValue.toLowerCase());
    return notSelected && matchesSearch;
  });

  // Check if we can create a new tag (search value doesn't match existing tags)
  const canCreateTag = onCreateTag && 
    searchValue.trim() !== "" && 
    !tagsList.some(t => t.tag?.toLowerCase() === searchValue.toLowerCase()) &&
    !tags.some(t => t.tag?.toLowerCase() === searchValue.toLowerCase());

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  const handleTagSelect = (tag) => {
    onAddTag(tag.id);
    setSearchValue("");
    setIsOpen(false);
  };

  const handleCreateTag = async () => {
    if (canCreateTag) {
      await onCreateTag(searchValue.trim());
      setSearchValue("");
      setIsOpen(false);
    }
  };

  const handleOpenDropdown = () => {
    setIsOpen(true);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  return (
    <div className="mobile-tag-dropdown" ref={containerRef}>
      {/* Selected Tags */}
      <div className="mobile-tag-dropdown__tags">
        {tags.length === 0 ? (
          <span className="mobile-tag-dropdown__empty">No tags assigned</span>
        ) : (
          tags.map((tag) => (
            <div key={tag.id} className="mobile-tag-dropdown__tag">
              <span>{tag.tag}</span>
              <CloseIcon 
                style={{ fontSize: 16 }} 
                onClick={() => onRemoveTag(tag.id)}
              />
            </div>
          ))
        )}
      </div>

      {/* Add Tag Button */}
      <button 
        className="mobile-tag-dropdown__add-btn"
        onClick={handleOpenDropdown}
        type="button"
      >
        + Add Tag
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="mobile-tag-dropdown__panel">
          {/* Search Input */}
          <div className="mobile-tag-dropdown__search">
            <SearchIcon style={{ fontSize: 20, color: 'var(--secondairy-medium-gray)' }} />
            <input
              ref={inputRef}
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder={placeholder}
              autoComplete="off"
            />
            {searchValue && (
              <CloseIcon 
                style={{ fontSize: 18, color: 'var(--secondairy-medium-gray)', cursor: 'pointer' }} 
                onClick={() => setSearchValue("")}
              />
            )}
          </div>

          {/* Tag Options List */}
          <div className="mobile-tag-dropdown__list">
            {availableTags.length === 0 && !canCreateTag ? (
              <div className="mobile-tag-dropdown__no-results">
                {searchValue ? "No matching tags found" : "No more tags available"}
              </div>
            ) : (
              <>
                {availableTags.map((tag) => (
                  <button
                    key={tag.id}
                    className="mobile-tag-dropdown__option"
                    onClick={() => handleTagSelect(tag)}
                    type="button"
                  >
                    {tag.tag}
                  </button>
                ))}
                
                {canCreateTag && (
                  <button
                    className="mobile-tag-dropdown__option mobile-tag-dropdown__option--create"
                    onClick={handleCreateTag}
                    type="button"
                  >
                    Create "{searchValue.trim()}"
                  </button>
                )}
              </>
            )}
          </div>

          {/* Close Button */}
          <button 
            className="mobile-tag-dropdown__close"
            onClick={() => setIsOpen(false)}
            type="button"
          >
            Done
          </button>
        </div>
      )}
    </div>
  );
}

